import Link from "next/link";
import { ArrowRight, Building2, MapPin, TrendingUp } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { OptimizedImage } from "@/components/ui/optimized-image";
import type { Company } from "@/lib/types";

interface CompanyCardProps {
  company: Company;
  brandCount?: number;
}

export function CompanyCard({ company, brandCount }: CompanyCardProps) {
  return (
    <Link href={`/companies/${company.slug}`}>
      <Card className="h-full hover:shadow-lg hover:-translate-y-1 hover:border-accent/30 transition-all group border-border p-0 overflow-hidden">
        <div className="relative h-36 w-full overflow-hidden bg-muted dark:bg-white/35 flex items-center justify-center p-4">
          {company.logo && (
            <OptimizedImage
              src={company.logo}
              alt={company.name}
              fallbackSrc="/placeholder-company.svg"
              fill
              className="object-contain"
              sizes="(max-width: 640px) 90vw, (max-width: 1024px) 40vw, (max-width: 1280px) 30vw, 20vw"
            />
          )}
        </div>
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4">
            <h3 className="font-semibold text-lg text-foreground group-hover:text-accent transition-colors">
              {company.name}
            </h3>
            <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-accent transition-colors flex-shrink-0 mt-1" />
          </div>
          <p className="text-sm text-muted-foreground mt-3 line-clamp-2">
            {company.description}
          </p>
          <div className="mt-6 space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Building2 className="h-4 w-4" />
              <span className="capitalize">{company.companyType}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4" />
              <span className="truncate">{company.headquarters}</span>
            </div>
            {company.stockTicker && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <TrendingUp className="h-4 w-4" />
                <span>
                  {company.stockExchange}: {company.stockTicker}
                </span>
              </div>
            )}
          </div>
          <div className="mt-6 pt-4 border-t border-border px-1">
            <p className="text-sm font-medium text-accent">
              {brandCount ?? company.brandSlugs?.length ?? 0} brand
              {(brandCount ?? company.brandSlugs?.length ?? 0) !== 1 ? "s" : ""} in portfolio
            </p>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
